import {
  AcceptanceStatus,
  InsightTableData,
  statusMap,
} from "@/pages/CareInsight/types";
import { SearchInput } from "@/components/tableComponents/SearchInput/SearchInput";
import {
  tableHeaderStyle,
  tableRowTextStyle,
} from "@/components/tableComponents/tableStyles";
import { handleKeyPress, tw } from "@/utils";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import StatusFilter from "./StatusFilter";
import { InsightStatusLabel } from "./InsightStatusLabel";
import dayjs from "dayjs";
import { KtasStatusLabel } from "./KtasStatusLabel";
import { TableSort } from "../../../components/tableComponents/TableSort/TableSort";
import { SortDirection, SortState } from "@/components/tableComponents/types";
import WarningIcon from "./warningIcon.svg?react";
import { useGetInsightList } from "./api";
import Loader from "@/components/Loader";
import { DelayRenderWrapper } from "@/components/DelayRenderWrapper/DelayRenderWrapper";
import { useInfiniteScroll } from "@/hooks/useInfinitiScroll";
import { useTransferCountsStore } from "@/store/TransferCounts";

interface CareInsightListProps {
  // props 정의
}

export const CareInsightList = (props: CareInsightListProps) => {
  const {} = props;
  const navigate = useNavigate();
  const { settransferStatusCounts } = useTransferCountsStore();

  const dataScrollRef = useRef<HTMLDivElement>(null);

  const [searchText, setSearchText] = useState<string>("");
  const [searchParamsText, setSearchParamsText] = useState<string>("");
  const [selectedStatus, setSeletedStatus] = useState<string>("all");
  const [sortState, setSortState] = useState<SortState>({
    columnKey: "",
    direction: "none",
  });

  const convertDirectcionToSortDir = (direction: SortDirection) => {
    switch (direction) {
      case "ascending":
        return "asc";
      case "descending":
        return "desc";
      case "none":
        return undefined;
    }
  };

  const {
    data,
    isSuccess,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useGetInsightList({
    pageSize: 30,
    filterStatus:
      selectedStatus === "all"
        ? undefined
        : (selectedStatus as AcceptanceStatus),
    searchTitle: searchParamsText,
    sortBy: sortState.columnKey !== "" ? sortState.columnKey : undefined,
    sortDir:
      sortState.columnKey !== ""
        ? convertDirectcionToSortDir(sortState.direction)
        : undefined,
  });

  useInfiniteScroll({
    scrollRef: dataScrollRef,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const handleSortChange = (columnKey: string) => {
    setSortState((prev) => {
      if (prev.columnKey !== columnKey) {
        return { columnKey, direction: "ascending" };
      }

      switch (prev.direction) {
        case "ascending":
          return { columnKey, direction: "descending" };
        case "descending":
          return { columnKey: "", direction: "none" };
        default:
          return { columnKey, direction: "ascending" };
      }
    });
  };

  // 프론트 임시 작성 코드, 원래 백엔드 API 호출을 통해서 이루어 져야함
  useEffect(() => {
    if (isSuccess) {
      const countTransferStatus = (
        data: InsightTableData[]
      ): Record<AcceptanceStatus, number> => {
        // Initialize the count object with all statuses set to 0
        const statusCount: Record<AcceptanceStatus, number> = {
          in_progress: 0,
          accepted: 0,
          rejected: 0,
          canceled: 0,
          transfer_confirmed: 0,
          transfer_completed: 0,
        };

        // Iterate over the data and count each transferStatus
        data.forEach((item) => {
          if (item.transferStatus in statusCount) {
            statusCount[item.transferStatus]++;
          }
        });

        return statusCount;
      };

      settransferStatusCounts(countTransferStatus(data.pages[0].result.data));
    }
  }, [data, isSuccess]);

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     refetch(); // 10초마다 실행
  //   }, 5000); // 10000ms = 10초

  //   return () => clearInterval(interval); // 컴포넌트 언마운트 시 정리
  // }, [refetch]); // refetch가 변경되면 다시 세팅

  return (
    <div className="w-full h-full">
      <header
        className={tw(
          "flex items-center w-full h-[76px] gap-[10px] bg-white px-[30px]",
          "border-solid border-b-[1px] border-gray-g50"
        )}
      >
        <div className="text-2xl font-semibold text-gray-g400">
          구급이송 요청 목록
        </div>
        <div className="text-base font-regular text-gray-g100">
          기관으로 요청된 이송 의뢰 요청목록입니다.
        </div>
      </header>
      <div
        className={tw(
          "flex items-center justify-between w-full h-[76px] bg-white px-[24px] py-[16px]",
          "border-solid border-b-[1px] border-gray-g30"
        )}
      >
        <StatusFilter
          status={transferStatusFilterData}
          onChange={(status) => {
            setSeletedStatus(status);
          }}
        />
        <SearchInput
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="이송 제목 검색"
          onKeyDown={handleKeyPress("Enter", () => {
            setSearchParamsText(searchText);
          })}
          onSearch={() => {
            setSearchParamsText(searchText);
          }}
        />
        {/* <button
          onClick={() => {
            refetch();
          }}
        >
          새로고침
        </button> */}
      </div>
      <div className={tw("h-[calc(100%-152px)] w-full bg-white")}>
        <div className="w-full h-full rounded-[20px]">
          <div className="w-full h-full">
            <div className="h-full w-full">
              {/* 데이터 테이블 */}
              <div className="w-full h-full">
                <div className={tw(tableHeaderStyle)}>
                  <div className="w-[180px] flex items-center">
                    Pre-KTAS 등급
                    <TableSort
                      isActive={sortState.columnKey === "preKtas"}
                      direction={
                        sortState.columnKey === "preKtas"
                          ? sortState.direction
                          : "none"
                      }
                      onSortChange={() => handleSortChange("preKtas")}
                    />
                  </div>
                  <div className="flex-430">제목</div>
                  <div className="flex-370 flex items-center">
                    의뢰 일자 (경과 시간)
                    <TableSort
                      isActive={sortState.columnKey === "duration"}
                      direction={
                        sortState.columnKey === "duration"
                          ? sortState.direction
                          : "none"
                      }
                      onSortChange={() => handleSortChange("duration")}
                    />
                  </div>
                  <div className="flex-230 flex items-center">
                    도착 잔여 시간 (거리)
                    <TableSort
                      isActive={sortState.columnKey === "remaingTime"}
                      direction={
                        sortState.columnKey === "remaingTime"
                          ? sortState.direction
                          : "none"
                      }
                      onSortChange={() => handleSortChange("remaingTime")}
                    />
                  </div>
                  <div className="w-[150px]">상태</div>
                  {/* <div className="flex-170">담당자</div> */}
                </div>
                <div
                  className="h-[calc(100%-62px)] w-full overflow-auto"
                  ref={dataScrollRef}
                >
                  {isLoading && (
                    <div className="w-full h-full flex items-center justify-center">
                      <DelayRenderWrapper delay={200}>
                        <Loader />
                      </DelayRenderWrapper>
                    </div>
                  )}
                  {isFetchingNextPage && (
                    <div className="w-full h-[50px]">
                      <Loader />
                    </div>
                  )}
                  {/* todo 스크롤 생기는 경우 헤더랑 간격 맞추기 */}
                  {isSuccess && data.pages[0].result.data.length !== 0 ? (
                    (data?.pages.flatMap((page) => page.result.data) ?? []).map(
                      (data) => {
                        const elapsedTime = dayjs().diff(
                          dayjs(data.requestTime),
                          "minute"
                        );
                        const emergency =
                          data.transferStatus === "in_progress" &&
                          ((elapsedTime > 5 &&
                            (data.preKtas === 1 || data.preKtas === 2)) ||
                            elapsedTime > 10);
                        const inProgress =
                          data.transferStatus !== "transfer_completed";
                        return (
                          <div
                            key={data.requestId}
                            className={tw(
                              tableRowTextStyle(),
                              "cursor-pointer",
                              emergency && "bg-[rgba(255,150,124,0.10)]"
                            )}
                            onClick={() => {
                              navigate(`${data.requestId}`);
                            }}
                          >
                            <div className="w-[180px]">
                              <KtasStatusLabel ktasLevel={data.preKtas} />
                            </div>
                            <div className="flex-430">{data.transferTitle}</div>
                            <div className={tw("flex-370")}>
                              {inProgress &&
                                `${dayjs(data.requestTime).format("YYYY-MM-DD HH:mm")} (${elapsedTime}분 경과)`}
                              {emergency && (
                                <div
                                  className={tw(
                                    "ml-auto flex items-center gap-[3px]",
                                    "text-sm font-medium text-[#D34521]"
                                  )}
                                >
                                  <WarningIcon />
                                  확인 필요
                                </div>
                              )}
                            </div>
                            <div className="flex-230">
                              {inProgress ? (
                                <>
                                  {`${data.restTime}분 `}
                                  <span className="text-gray-g200">{`(${data.restDistance}km)`}</span>
                                </>
                              ) : (
                                "-"
                              )}
                            </div>
                            <div className="w-[150px]">
                              <InsightStatusLabel
                                status={data.transferStatus}
                              />
                            </div>
                            {/* <div className="flex-170">
                              {data.transferManager}
                            </div> */}
                          </div>
                        );
                      }
                    )
                  ) : (
                    <div className="flex justify-center w-full text-base font-semibold text-gray-g300 mt-[40px]">
                      구급이송 요청 환자가 없습니다.
                    </div>
                  )}
                  {isError && <div>error</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const transferStatusFilterData: {
  key: AcceptanceStatus | "all";
  value: string;
}[] = [
  { key: "all", value: "전체" },
  { key: "in_progress", value: statusMap["in_progress"] },
  { key: "accepted", value: statusMap["accepted"] },
  { key: "rejected", value: statusMap["rejected"] },
  { key: "canceled", value: statusMap["canceled"] },
  { key: "transfer_confirmed", value: statusMap["transfer_confirmed"] },
  { key: "transfer_completed", value: statusMap["transfer_completed"] },
];
